import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Form } from 'react-bootstrap';
import Select from 'react-select';
import { useFormikContext } from 'formik';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import {
  listarFornecedoresImobilizado,
  listarContasImobilizado,
  listarMoedas,
  buscarDepartamentos,
  listarGruposEmpresaImobilizado,
} from '~/Services/Service';

const sectionHeaderStyle = {
  background: '#0747a6',
  color: '#fff',
  padding: '6px 12px',
  fontWeight: 700,
  fontSize: 13,
  borderRadius: '4px 4px 0 0',
};

const labelStyle = { fontSize: 12, fontWeight: 600, marginBottom: 2 };

const SecaoDadosCompra = () => {
  const { values, setFieldValue, errors, touched } = useFormikContext();

  const [opcoesGE, setOpcoesGE] = useState([]);
  const [opcoesFornecedores, setOpcoesFornecedores] = useState([]);
  const [opcoesContas, setOpcoesContas] = useState([]);
  const [opcoesMoedas, setOpcoesMoedas] = useState([]);
  const [opcoesDepartamentos, setOpcoesDepartamentos] = useState([]);

  // Carga dos Grupos_Empresa do usuário logado (replica cdge do Delphi
  // UImobilizado.pas:627-633). Dispara no FormShow equivalente.
  useEffect(() => {
    let ativo = true;
    listarGruposEmpresaImobilizado()
      .then(res => {
        if (!ativo) return;
        const lista = Array.isArray(res) ? res : [];
        const opcoes = lista.map(ge => ({
          value: ge.codigo_grupo_empresa,
          label: `${ge.codigo_grupo_empresa} — ${ge.apelido}`,
        }));
        setOpcoesGE(opcoes);
        // Auto-select quando usuário tem apenas uma GE
        if (opcoes.length === 1 && !values.codigoGrupoEmpresa) {
          setFieldValue('codigoGrupoEmpresa', opcoes[0].value);
        }
      })
      .catch(() => {});
    return () => { ativo = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Carga de moedas e departamentos (sem dependência de GE)
  useEffect(() => {
    let ativo = true;
    listarMoedas()
      .then(res => {
        if (!ativo) return;
        const lista = Array.isArray(res) ? res : [];
        setOpcoesMoedas(lista.map(m => ({ value: m.codigo_ativo, label: m.nome })));
        // Default moeda = 11 (Real)
        if (!values.codigoMoeda) {
          const real = lista.find(m => m.codigo_ativo === 11);
          if (real) setFieldValue('codigoMoeda', 11);
        }
      })
      .catch(() => {});
    buscarDepartamentos()
      .then(res => {
        if (!ativo) return;
        const lista = Array.isArray(res) ? res : [];
        setOpcoesDepartamentos(lista.map(d => ({ value: d.codigo, label: d.descricao })));
      })
      .catch(() => {});
    return () => { ativo = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Cascateamento GE → fornecedores e contas
  useEffect(() => {
    if (!values.codigoGrupoEmpresa) {
      setOpcoesFornecedores([]);
      setOpcoesContas([]);
      return;
    }
    let ativo = true;
    listarFornecedoresImobilizado(values.codigoGrupoEmpresa)
      .then(res => {
        if (!ativo) return;
        const lista = Array.isArray(res) ? res : [];
        setOpcoesFornecedores(
          lista.map(f => ({
            value: f.codigo != null ? f.codigo : f.codigo_conta,
            label: f.apelido || f.apelido_conta || f.razao_social || `${f.codigo}`,
          }))
        );
      })
      .catch(() => {});
    listarContasImobilizado(values.codigoGrupoEmpresa)
      .then(res => {
        if (!ativo) return;
        const lista = Array.isArray(res) ? res : [];
        setOpcoesContas(
          lista.map(c => ({ value: c.codigo_conta, label: c.apelido_conta }))
        );
      })
      .catch(() => {});
    return () => { ativo = false; };
  }, [values.codigoGrupoEmpresa]);

  const geValue = opcoesGE.find(o => o.value === values.codigoGrupoEmpresa) || null;
  const fornValue = opcoesFornecedores.find(o => o.value === values.codigoFornecedor) || null;
  const contaValue = opcoesContas.find(o => o.value === values.codigoContaImobilizado) || null;
  const moedaValue = opcoesMoedas.find(o => o.value === values.codigoMoeda) || null;
  const deptoValue = opcoesDepartamentos.find(o => o.value === values.codigoDepartamento) || null;

  return (
    <Card className="shadow-sm mb-3">
      <div style={sectionHeaderStyle}>Dados da Compra</div>
      <Card.Body>
        <Row className="mb-2">
          <Col md={4} sm={12} className="mb-2">
            <Form.Label style={labelStyle}>Grupo Empresa *</Form.Label>
            <Select
              styles={customStyles}
              components={{ DropdownIndicator }}
              options={opcoesGE}
              value={geValue}
              onChange={opt => {
                setFieldValue('codigoGrupoEmpresa', opt ? opt.value : null);
                setFieldValue('codigoFornecedor', null);
                setFieldValue('codigoContaImobilizado', null);
              }}
              placeholder="Selecione..."
              isClearable
              menuPortalTarget={document.body}
            />
            {touched.codigoGrupoEmpresa && errors.codigoGrupoEmpresa && (
              <small className="text-danger">{errors.codigoGrupoEmpresa}</small>
            )}
          </Col>
          <Col md={4} sm={12} className="mb-2">
            <Form.Label style={labelStyle}>Fornecedor *</Form.Label>
            <Select
              styles={customStyles}
              components={{ DropdownIndicator }}
              options={opcoesFornecedores}
              value={fornValue}
              onChange={opt => setFieldValue('codigoFornecedor', opt ? opt.value : null)}
              placeholder="Selecione..."
              isClearable
              isDisabled={!values.codigoGrupoEmpresa}
              menuPortalTarget={document.body}
            />
            {touched.codigoFornecedor && errors.codigoFornecedor && (
              <small className="text-danger">{errors.codigoFornecedor}</small>
            )}
          </Col>
          <Col md={4} sm={12} className="mb-2">
            <Form.Label style={labelStyle}>Conta Imobilizado *</Form.Label>
            <Select
              styles={customStyles}
              components={{ DropdownIndicator }}
              options={opcoesContas}
              value={contaValue}
              onChange={opt => setFieldValue('codigoContaImobilizado', opt ? opt.value : null)}
              placeholder="Selecione..."
              isClearable
              isDisabled={!values.codigoGrupoEmpresa}
              menuPortalTarget={document.body}
            />
            {touched.codigoContaImobilizado && errors.codigoContaImobilizado && (
              <small className="text-danger">{errors.codigoContaImobilizado}</small>
            )}
          </Col>
        </Row>

        <Row className="mb-2">
          <Col md={3} sm={6} className="mb-2">
            <Form.Label style={labelStyle}>Moeda *</Form.Label>
            <Select
              styles={customStyles}
              components={{ DropdownIndicator }}
              options={opcoesMoedas}
              value={moedaValue}
              onChange={opt => setFieldValue('codigoMoeda', opt ? opt.value : null)}
              placeholder="Selecione..."
              menuPortalTarget={document.body}
            />
            {touched.codigoMoeda && errors.codigoMoeda && (
              <small className="text-danger">{errors.codigoMoeda}</small>
            )}
          </Col>
          <Col md={4} sm={6} className="mb-2">
            <Form.Label style={labelStyle}>Departamento</Form.Label>
            <Select
              styles={customStyles}
              components={{ DropdownIndicator }}
              options={opcoesDepartamentos}
              value={deptoValue}
              onChange={opt => setFieldValue('codigoDepartamento', opt ? opt.value : null)}
              placeholder="Selecione (opcional)..."
              isClearable
              menuPortalTarget={document.body}
            />
          </Col>
        </Row>

        <Row>
          {['observacao1', 'observacao2', 'observacao3', 'observacao4'].map((campo, idx) => (
            <Col md={3} sm={6} key={campo} className="mb-2">
              <Form.Label style={labelStyle}>Observação {idx + 1}</Form.Label>
              <Form.Control
                as="textarea"
                rows={1}
                maxLength={200}
                value={values[campo] || ''}
                onChange={e => setFieldValue(campo, e.target.value)}
                placeholder="Opcional, mas pelo menos uma é obrigatória"
              />
            </Col>
          ))}
        </Row>
        {touched.observacao1 && errors.observacao1 && (
          <small className="text-danger">{errors.observacao1}</small>
        )}
      </Card.Body>
    </Card>
  );
};

export default SecaoDadosCompra;
